import { getUID } from '@redviking/argonaut-util/src/getUID';
import { type NodeCfg, nodeCfgSchemaMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { NodeSpec } from './NodeSpec';
import { defineControlOpts } from '../../controls/FlowNodeControl';

export const jsonParseNodeSpec: NodeSpec<NodeCfg<'jsonParse'>> = {
    requiredSockets: (_, _availableInputSockets, availableOutputSockets) => ({
        required: [ 'input' ],
        conditionallyRequired: [ { kind: 'or', availableOptions: availableOutputSockets } ],
    }),
    cfg: () => ({ controlValues: { outputExpressions: [] }, id: getUID(), kind: 'jsonParse', disabled: false }),
    controls: nodeCfg => ({
        outputExpressions: defineControlOpts({
            cfg: {
                kind: 'button',
                useDialog: null,
                label: 'Add Output',
            },
            on: {
                'update:value': () => {}, // no-op, this control does not use the update:value event
                'click': (_, { flowEventEmitter }) => {
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                outputExpressions: [ ...nodeCfg.controlValues.outputExpressions, '' ],
                            },
                        },
                    });
                },
            },
            value: nodeCfg.controlValues.outputExpressions,
        }),
    }),
    inputs: () => ({
        input: { socket: 'string', label: 'Input' },
        trigger: { socket: 'event', label: 'Trigger' },
    }),
    nodeCategory: 'other',
    kindLabel: 'JSON Parse',
    outputs: nodeCfg => {
        const outputs: ReturnType<NodeSpec<NodeCfg<'jsonParse'>>['outputs']> = {
            next: { socket: 'event', label: 'Next' },
        };
        for (let index = 0; index < nodeCfg.controlValues.outputExpressions.length; index++) {
            outputs[`output${index}`] = {
                controlOpts: defineControlOpts({
                    cfg: {
                        kind: 'deletableSocket',
                        childControl: defineControlOpts({
                            cfg: { kind: 'text', placeholder: 'JSONata Expression' },
                            value: nodeCfg.controlValues.outputExpressions[index],
                            enableWithConnectedSocket: true,
                            on: {
                                'update:value': (newValue, { flowEventEmitter }) => {
                                    const outputExpressions = [ ...nodeCfg.controlValues.outputExpressions ];
                                    outputExpressions[index] = newValue;
                                    flowEventEmitter.emit('updateNodeCfg', {
                                        newNodeCfg: {
                                            ...nodeCfg,
                                            controlValues: { ...nodeCfg.controlValues, outputExpressions },
                                        },
                                    });
                                },
                            },
                        }),
                    },
                    value: null,
                    enableWithConnectedSocket: true,
                    on: {
                        'update:value': () => {}, // no-op, this control does not use the update:value event
                        'delete': (_, { flowEventEmitter }) => {
                            flowEventEmitter.emit('removeNodeSocket', {
                                newNodeCfg: {
                                    ...nodeCfg,
                                    controlValues: {
                                        ...nodeCfg.controlValues,
                                        outputExpressions: nodeCfg.controlValues.outputExpressions.filter((_, outputIndex) => outputIndex !== index),
                                    },
                                },
                                direction: 'output',
                                removalMethod: { kind: 'indexedIdentifier', prefix: 'output', index },
                            });
                        },
                    },
                }),
                socket: 'varValue',
            };
        }
        return outputs;
    },
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.outputExpressions.length) {
            errors.outputExpressions = 'At least one output expression is required';
        }
        for (let index = 0; index < node.controlValues.outputExpressions.length; index++) {
            const expression = node.controlValues.outputExpressions[index];
            if (!expression) {
                errors[`output${index}`] = 'Output expression is required';
            } else if (nodeCfgSchemaMap.jsonParse.shape.controlValues.shape.outputExpressions.safeParse([ expression ]).success === false) {
                // I think there is a call to safe parse on the entire config that is occuring elsewhere essentially duplicating this check
                errors[`output${index}`] = 'This is not a valid JSONata expression';
            }
        }
        return Object.keys(errors).length ? errors : true;
    },
};
